interface GetPropOptions {
  separator?: string;
  defaultValue?: any | undefined;
  skipProps?: string[];
}

interface SetPropOptions {
  numericPathAsArray?: boolean;
  separator?: string;
  skipProps?: string[];
}

type Paths<T, D extends number = 5> = [D] extends [never]
  ? never
  : T extends object
  ? {
      [K in keyof T]-?: K extends string | number
        ? //@ts-ignore
          `${K}` | Join<K, Paths<T[K], Prev[D]>>
        : never;
    }[keyof T]
  : "";

var defaultSetOptions = {
  numericPathAsArray: false,
  separator: ".",
  skipProps: [],
};

var defaultGetOptions = {
  separator: ".",
  defaultValue: undefined,
  skipProps: [],
};

/**
 *
 * Makes a deep object clone
 * @param obj Object to copy
 * @returns Cloned Object
 *
 */
export function cloneDeep<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 *
 * Sets object property given a path
 * @param path Property path separated with separator (default: '.')
 * @param data Data to set
 * @param obj Object for which to set
 * @param extraOptions Path parsing options (optional)
 *
 */
export function setProp<T, P = Paths<T>>(
  path: P,
  data: T,
  obj: any,
  extraOptions?: SetPropOptions
) {
  var options = Object.assign({}, defaultSetOptions, extraOptions);
  // @ts-ignore
  var pathParts = path.split(options.separator);
  while (pathParts.length > 1) {
    var part = pathParts.shift();
    if (options.skipProps.indexOf(part) !== -1) {
      continue;
    }
    if (obj[part] === null || obj[part] === undefined) {
      if (options.numericPathAsArray && !isNaN(parseInt(part, 10))) {
        obj[part] = [];
      } else {
        obj[part] = {};
      }
    }
    obj = obj[part];
  }
  obj[pathParts[0]] = data;
}

/**
 *
 * Sets object property given a path and returns a copy of the object
 * @param path Property path separated with separator (default: '.')
 * @param data Data to set
 * @param obj Object for which to set
 * @param extraOptions Path parsing options (optional)
 * @returns Object with property set
 *
 */
export function setPropImmutable<T, P = Paths<T>>(
  path: P,
  data: any,
  obj: T,
  extraOptions?: SetPropOptions
): T {
  var options = Object.assign({}, defaultSetOptions, extraOptions);
  obj = cloneDeep(obj);
  setProp(path, data, obj, options);
  return obj;
}

/**
 *
 * Gets value from object given a path
 * @param path Property path separated with separator (default: '.')
 * @param obj Data from where to get
 * @param extraOptions Path parsing options (optional)
 * @returns Data from object or default value if data is not found
 *
 */
export function getProp<T, O, P = Paths<O>>(
  path: P,
  obj: O,
  extraOptions?: GetPropOptions
): T {
  var options = Object.assign({}, defaultGetOptions, extraOptions);
  // @ts-ignore
  var pathParts = path.split(options.separator);
  while (pathParts.length > 0) {
    var part = pathParts.shift();
    if (options.skipProps.indexOf(part) !== -1) {
      continue;
    }
    // @ts-ignore
    if (obj[part] === null || obj[part] === undefined) {
      return options.defaultValue;
    }
    // @ts-ignore
    obj = obj[part];
  }
  if (obj === null || obj === undefined) return options.defaultValue;
  // @ts-ignore
  return obj;
}

/**
 *
 * Map data from input event into object
 * @param e InputEvent
 * @returns Object in format {inputName: inputValue}
 *
 */
export function mapInputEventData(e: React.ChangeEvent<HTMLInputElement>) {
  return { [e.target.name]: e.target.value };
}
